import React, { useEffect, useRef } from "react";
import { useLeaflet } from "../../../hooks/useLeaflet";
import { textTooltip } from "../../../lib/leafletText";
import { statusText } from "../model/shipmentStatus";
import type { TowerShipment } from "../../../types/controlTower";

export const FOCUS_COLORS = { target: "#e8b62c", route: "#76a9c9", core: "#9fd3f0" };

type Core = { latitude: number; longitude: number; radiusKm: number };

// Mapa con un envío destacado (amarillo) sobre los envíos de un chofer
// (celeste). Con `core`, además el núcleo habitual del reparto y la línea
// punteada que mide la separación (no es un recorrido). Lo usan las alertas
// geográficas y Sin asignar.
// Leaflet interpreta un string como HTML: los datos importados (tracking,
// cliente) van siempre como texto (lib/leafletText).

// Desarma un mapa de Leaflet cortando antes sus animaciones y eventos: al
// cambiar rápido de vista, una animación pendiente sobre un mapa ya quitado
// tira "Script error." (Leaflet viene de un CDN).
export function removeMap(map: any) {
  try {
    map.stop();
    map.off();
    map.remove();
  } catch {
    // El mapa ya estaba a medio desarmar: no queda nada que limpiar.
  }
}

export default function TowerFocusMap({
  target,
  others,
  core = null,
  targetLabel,
}: {
  target: TowerShipment;
  others: TowerShipment[];
  core?: Core | null;
  targetLabel: string;
}) {
  const leaflet = useLeaflet();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  // Primitivos: un `core` nuevo en cada render no tiene que redibujar ni reencuadrar.
  const coreLat = core?.latitude ?? null;
  const coreLng = core?.longitude ?? null;
  const coreKm = core?.radiusKm ?? 0;

  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = (window as any).L;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true }).setView(
      [-34.6, -58.42],
      10,
    );
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    // Si el contenedor cambia de ancho (por ejemplo, al plegar el menú lateral),
    // Leaflet recalcula su tamaño.
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => map.invalidateSize()) : null;
    observer?.observe(containerRef.current);
    return () => {
      observer?.disconnect();
      removeMap(map);
      mapRef.current = null;
      layerRef.current = null;
    };
  }, [leaflet]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!leaflet || !map || !layer) return;
    const L = (window as any).L;
    layer.clearLayers();
    const bounds: [number, number][] = [];
    for (const s of others) {
      if (s.latitude === null || s.longitude === null || s.id === target.id) continue;
      L.circleMarker([s.latitude, s.longitude], {
        radius: 6,
        color: "#0d0e10",
        weight: 2,
        fillColor: FOCUS_COLORS.route,
        fillOpacity: 0.9,
      })
        .bindTooltip(textTooltip(`${s.tracking} · ${statusText(s)}`), { direction: "top" })
        .addTo(layer);
      bounds.push([s.latitude, s.longitude]);
    }
    const located = target.latitude !== null && target.longitude !== null;
    if (coreLat !== null && coreLng !== null && located) {
      const center: [number, number] = [coreLat, coreLng];
      L.circle(center, {
        radius: Math.max(300, coreKm * 1000),
        color: FOCUS_COLORS.core,
        weight: 2,
        dashArray: "6 6",
        fillColor: FOCUS_COLORS.core,
        fillOpacity: 0.08,
      })
        .bindTooltip("Núcleo habitual", { direction: "top" })
        .addTo(layer);
      L.circleMarker(center, {
        radius: 5,
        color: "#0d0e10",
        weight: 2,
        fillColor: FOCUS_COLORS.core,
        fillOpacity: 1,
      }).addTo(layer);
      L.polyline([center, [target.latitude, target.longitude]], {
        color: FOCUS_COLORS.target,
        weight: 2,
        dashArray: "6 8",
        opacity: 0.9,
      }).addTo(layer);
      bounds.push(center);
    }
    if (located) {
      L.circleMarker([target.latitude, target.longitude], {
        radius: 11,
        color: "#0d0e10",
        weight: 3,
        fillColor: FOCUS_COLORS.target,
        fillOpacity: 1,
      })
        .bindTooltip(textTooltip(`${targetLabel} · ${target.tracking}`), { direction: "top" })
        .addTo(layer);
      bounds.push([target.latitude as number, target.longitude as number]);
    }
    // Sin animación: si se cambia de vista a mitad de un encuadre animado, Leaflet
    // lo termina sobre un mapa ya desarmado y falla.
    if (bounds.length === 1) map.setView(bounds[0], 13, { animate: false });
    else if (bounds.length > 1) map.fitBounds(L.latLngBounds(bounds).pad(0.1), { maxZoom: 14, animate: false });
    const frame = window.requestAnimationFrame(() => {
      if (mapRef.current === map) map.invalidateSize();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [leaflet, target, others, coreLat, coreLng, coreKm, targetLabel]);

  return !leaflet ? (
    <div className="tower-map-placeholder">Cargando mapa…</div>
  ) : (
    <div ref={containerRef} className="tower-geo-map" aria-label="Mapa" />
  );
}
